import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { MODALS } from "../../components/organisms/AppModals/modals.constants";
import { actions as breadCrumbActions } from "../../components/organisms/BreadCrumb/breadCrumb.reducers";
import { actions as modalActions } from "../../components/organisms/Modal/modal.reducers";
import { actions as appActions } from "../Applications/applications.reducers";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import { selectUser } from "./userProfile.selectors";
import {
  EMPTY_STRING,
  API_RESPONSES,
  ALERT_TIMEOUT,
  IMAGE_SIZE_EXCEED_MESSAGE,
  UPLOAD_LOGO_SIZE,
  IMAGE_ALLOWED_TYPES,
  IMAGE_INVALID_FORMAT_MESSAGE,
  ONE,
  USER_ROLES,
  ALERT_MESSAGES,
} from "../../constants";
import { profilePic } from "../../assets/images";
import {
  menuDeleteIcon,
  menuEditIcon,
  chevronLeftIcon,
} from "../../assets/icons";
import { updateUserProfile } from "./userProfile.actions";
import { deleteUserAccount, getBase64FromFile } from "../../helpers";
import Alert from "../../components/organisms/Alert";
import DeleteConfirmationModal from "../../components/organisms/AppModals/DeleteConfirmationModal";
import { selectUsers } from "../Users";

const { UPDATE_PASSWORD_MODAL, DELETE_MODAL, TRANSFER_OWNERSHIP_MODAL } =
  MODALS;
const { setSelectedModal, setItemToBeDeleted } = modalActions;
const { setSelectedTab } = breadCrumbActions;
const { setShouldShowLeftPane } = appActions;
const { UPDATE_USER_PROFILE, DELETE_USER } = SPINNER_NAMES;
const { ERROR, SUCCESS } = API_RESPONSES;
const { OWNER } = USER_ROLES;
const { PROFILE_UPDATED, PROFILE_NOT_UPDATED } = ALERT_MESSAGES;

const UserProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState({
    name: EMPTY_STRING,
    email: EMPTY_STRING,
    organizations: { name: EMPTY_STRING },
    role: EMPTY_STRING,
    joinedDate: EMPTY_STRING,
    profilePic: EMPTY_STRING,
    profilePicUrl: EMPTY_STRING,
  });

  const userProfile = useSelector(selectUser);
  const { data: users } = useSelector(selectUsers);
  const [joinedDate, setJoinedDate] = useState(EMPTY_STRING);
  const [isNameEditable, setIsNameEditable] = useState(false);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(true);
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);
  const [alertData, setAlertData] = useState();
  const [shouldShowDeleteModal, setShouldShowDeleteModal] = useState(false);

  const isCurrentUserOwner = userProfile.role === OWNER;

  useEffect(() => {
    if (userProfile) {
      setCurrentUser(userProfile);
    }
  }, [userProfile]);

  const navigateToPrevPage = () => navigate(-1);

  useEffect(() => {
    const joiningDate = new Date(userProfile.createdAt);
    const parsedDate = moment.utc(joiningDate);
    setJoinedDate(
      parsedDate.date() +
        "/" +
        (parsedDate.month() + 1) +
        "/" +
        parsedDate.year()
    );
  }, [userProfile.createdAt]);

  useEffect(() => {
    setIsSubmitButtonDisabled(
      (userProfile.profilePicUrl === currentUser.profilePicUrl &&
        userProfile.name === currentUser.name.trim()) ||
        currentUser.name.trim() === EMPTY_STRING
    );
  }, [userProfile, currentUser]);

  useEffect(() => {
    dispatch(setShouldShowLeftPane(false));
    dispatch(setSelectedTab(null));
    return () => {
      dispatch(setShouldShowLeftPane(true));
    };
  }, [dispatch]);

  const updateAvatar = async (event) => {
    const file = event.target.files[0];
    if (file.size > UPLOAD_LOGO_SIZE) {
      setAlertData({ message: IMAGE_SIZE_EXCEED_MESSAGE });
      return false;
    } else if (!IMAGE_ALLOWED_TYPES.includes(file.type)) {
      setAlertData({ message: IMAGE_INVALID_FORMAT_MESSAGE });
      return false;
    } else {
      const base64img = await getBase64FromFile(file);
      setCurrentUser({
        ...currentUser,
        profilePicUrl: base64img,
        profilePic: base64img,
        fileName: file.name,
      });
    }
  };

  const removeAvatar = () => {
    setCurrentUser({
      ...currentUser,
      profilePicUrl: EMPTY_STRING,
      profilePic: EMPTY_STRING,
      fileName: EMPTY_STRING,
    });
  };

  const handleChange = (event) => {
    setCurrentUser({
      ...currentUser,
      name: event.target.value.replace(/[^A-Za-z ]/g, EMPTY_STRING),
    });
  };

  const handleReset = () => {
    setCurrentUser(userProfile);
    setIsNameEditable(false);
  };

  const handleSubmit = useCallback(() => {
    setIsNameEditable(false);
    const patchUserData = { ...currentUser };

    if (patchUserData.userApplicationAssociations)
      delete patchUserData.userApplicationAssociations;
    if (patchUserData.organizations) delete patchUserData.organizations;
    dispatch(
      updateUserProfile({
        id: currentUser.id,
        user: patchUserData,
      })
    ).then((res) => {
      if (res.payload)
        setAlertData({
          type: SUCCESS,
          message: PROFILE_UPDATED,
        });
      else
        setAlertData({
          type: ERROR,
          message: PROFILE_NOT_UPDATED,
        });
    });
  }, [currentUser, dispatch]);

  const handleOnDelete = () => {
    dispatch(setItemToBeDeleted(currentUser));
    dispatch(setSelectedModal(DELETE_MODAL));
    setShouldShowDeleteModal(true);
  };

  const handleTransferOwnership = () =>
    dispatch(setSelectedModal(TRANSFER_OWNERSHIP_MODAL));

  return (
    <div className="mx-auto">
      <Helmet>
        <title>User Profile | TavaJS</title>
      </Helmet>
      <div>
        {alertData && (
          <div className="mr-[5px] ml-12 mt-4 -mb-6">
            <Alert
              type={alertData.type}
              message={alertData.message}
              handleOnClose={setAlertData}
              timeout={ALERT_TIMEOUT}
            />
          </div>
        )}
        <div className="overflow-y-scroll no-scrollbar lg:flex w-[90vw] h-[80vh] justify-around mt-10 text-white">
          <button
            className="h-12 rounded-md hover:bg-gray-700/60"
            onClick={navigateToPrevPage}
          >
            <img
              src={chevronLeftIcon}
              className="w-12"
              alt="chevron-left icon"
            />
          </button>
          <div className="flex flex-col justify-center w-full h-fit lg:w-[30%] lg:h-[90%] py-10 px-4 mb-4 bg-gray-800 border-gray-400 rounded-lg">
            <label htmlFor="update-avatar">
              <img
                src={currentUser.profilePicUrl || profilePic}
                alt="User Profile Pic"
                className="rounded-full w-60 h-60 object-cover mx-auto ring-2 ring-gray-400 cursor-pointer hover:opacity-70"
              />
            </label>
            <div className="flex my-4 mx-auto mb-7 text-center w-fit space-x-3">
              <label htmlFor="update-avatar">
                <img
                  src={menuEditIcon}
                  alt="Update Icon"
                  className="h-6 cursor-pointer hover:shadow-md hover:shadow-gray-600"
                  title="Update Profile Photo"
                />
              </label>
              <input
                key={currentUser.profilePic}
                type="file"
                accept="image/*"
                name="update-avatar"
                id="update-avatar"
                className="hidden"
                onChange={updateAvatar}
                onClick={(event) => (event.target.value = null)}
              />
              <img
                src={menuDeleteIcon}
                alt="Delete Icon"
                className="h-6 cursor-pointer hover:shadow-md hover:shadow-gray-600"
                onClick={removeAvatar}
                title="Remove Profile Photo"
              />
            </div>
            <div className="font-bold w-fit mx-auto max-w-full ">
              {userProfile.name}
            </div>
            <div className="font-bold w-fit mx-auto max-w-full ">
              {userProfile.email}
            </div>
          </div>
          <div className="flex flex-col justify-center w-full h-fit min-h-[50%] lg:w-[65%] lg:h-[90%] p-5 bg-gray-800 border-gray-400 rounded-lg">
            <div className="mb-5 text-xl font-bold">User Details</div>
            <div className="space-y-4 md:space-y-7 md:ml-5">
              <div className="md:grid grid-cols-2 md:space-x-11 space-y-4 md:space-y-0">
                <div className="row-auto space-y-2">
                  <div className="font-bold">Name:</div>
                  <div
                    className={`flex justify-between p-2 border-[1px] border-gray-400 rounded-md ${
                      isNameEditable ? "ring-1 ring-blue-500" : EMPTY_STRING
                    }`}
                  >
                    <input
                      type="text"
                      className="w-full bg-inherit p-0 border-0 enabled:ring-0 overflow-x-scroll"
                      value={currentUser.name}
                      onInput={handleChange}
                      disabled={!isNameEditable}
                    />
                    <img
                      src={menuEditIcon}
                      alt="Edit Name"
                      className="h-6 cursor-pointer hover:shadow-md hover:shadow-gray-600"
                      onClick={() => setIsNameEditable(!isNameEditable)}
                      title="Edit Name"
                    />
                  </div>
                </div>
                <div className="row-auto space-y-2">
                  <div className="font-bold">Email:</div>
                  <div className="p-2 border-[1px] border-gray-600  bg-gray-700 text-gray-400 rounded-md overflow-x-scroll sm:no-scrollbar">
                    {currentUser.email}
                  </div>
                </div>
              </div>
              <div className="md:grid grid-cols-2 md:space-x-11 space-y-4 md:space-y-0 text-l">
                <div className="row-auto space-y-2">
                  <div className="font-bold">Password:</div>
                  <div className="flex justify-between p-2 border-[1px] border-gray-400 rounded-md">
                    ********
                    <img
                      src={menuEditIcon}
                      alt="Update Password"
                      className="h-6 cursor-pointer hover:shadow-md hover:shadow-gray-600"
                      onClick={() =>
                        dispatch(setSelectedModal(UPDATE_PASSWORD_MODAL))
                      }
                      title="Update Password"
                    />
                  </div>
                </div>
                <div className="row-auto space-y-2">
                  <div className="font-bold">Role:</div>
                  <div className="p-2 border-[1px] border-gray-600 bg-gray-700 text-gray-400 capitalize rounded-md">
                    {currentUser.role}
                  </div>
                </div>
              </div>
              <div className="md:grid grid-cols-2 md:space-x-11 space-y-4 md:space-y-0 text-l">
                <div className="row-auto space-y-2">
                  <div className="font-bold">Joined On:</div>
                  <div className="p-2 border-[1px] border-gray-600 bg-gray-700 text-gray-400 rounded-md">
                    {joinedDate}
                  </div>
                </div>
              </div>
              <div className="mt-8 flex align-center justify-between">
                <div>
                  <button
                    className="btn btn-danger mr-2"
                    onClick={handleOnDelete}
                  >
                    Delete Account
                  </button>
                  {isCurrentUserOwner && users?.length > ONE && (
                    <button
                      className="btn btn-primary-light"
                      onClick={handleTransferOwnership}
                    >
                      Transfer Ownership
                    </button>
                  )}
                </div>
                <div className="flex">
                  <button
                    disabled={isSubmitButtonDisabled || isSpinnerActive}
                    className="btn btn-primary-light mr-2"
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                  <button
                    disabled={isSubmitButtonDisabled || isSpinnerActive}
                    className="btn btn-primary"
                    onClick={handleSubmit}
                  >
                    <Spinner
                      name={UPDATE_USER_PROFILE}
                      setIsSpinnerActive={setIsSpinnerActive}
                      persistSize
                    >
                      Save
                    </Spinner>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {shouldShowDeleteModal && (
        <DeleteConfirmationModal
          handleDelete={(user) =>
            deleteUserAccount(user, dispatch, DELETE_USER)
          }
          setShouldShowDeleteModal={setShouldShowDeleteModal}
          title="Delete Account"
          spinner={DELETE_USER}
        />
      )}
    </div>
  );
};
export default UserProfile;
